import React, { useEffect, useRef, useState } from "react";
import { useOfficerDetails } from "./OfficerDetails";
import useOfficersForm from "../useOfficersForm";
import API_ROUTES from "../../../api-routes";
import apiService from "../../../services/service";
import { z } from "zod";

const useOfficerPersonalInfo = (type) => {
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const formResetRef = useRef(null);
  const [isAuditSidebarVisible, setAuditSidebarVisible] = useState(false);

  const {
    regData: initialOfficerData,
    setRegData,
    resetRegData: resetOfficerData,
  } = useOfficerDetails();

  const [regData, localSetRegData] = useState(initialOfficerData || {});
  const { btnToggle, setBtnToggle, formDetails, setFormDetails } =
    useOfficersForm({
      type,
      setShowDialog,
      regData,
      setRegData: localSetRegData,
      resetRegData: resetOfficerData,
    });

  useEffect(() => {
    localSetRegData(initialOfficerData || {});
    setFormDetails(initialOfficerData || {});
  }, [initialOfficerData]);

  useEffect(() => {
    setFormDetails(regData);
    setIsSaveDisabled(
      JSON.stringify(regData) === JSON.stringify(initialOfficerData),
    );
  }, [regData, initialOfficerData]);

  const handleEditClick = () => {
    setIsEditing(true);
    setSuccessMessage("");
    setIsSaveDisabled(true);
  };

  const handleDiscardClick = () => {
    resetOfficerData(initialOfficerData);
    localSetRegData(initialOfficerData);
    setFormDetails(initialOfficerData);
    setIsEditing(false);
    if (formResetRef.current) {
      formResetRef.current(initialOfficerData);
    }
  };

  const handleSaveClick = async () => {
    setIsLoading(true);
    try {
      const updatedOfficerData = {
        name: regData.name || "",
        other_name: regData.other_name || "",
        last_name: regData.last_name || "",
        preferred_name: regData.preferred_name || "",
        role:
          typeof regData.role === "object"
            ? regData.role.name
            : regData.role || "",
        title: regData.title || "",
        email: regData.email || "",
        country:
          typeof regData.country === "object"
            ? regData.country.code
            : regData.country || "",
        phone_code: regData.phone_code || "",
        phone: regData.phone || "",
        type,
        entity: regData.entity || "",
        expiration_date:
          type === "power-of-attorney" ? regData.expiration_date : null,
        acting_as:
          type === "power-of-attorney"
            ? regData.acting_as && typeof regData.acting_as === "object"
              ? regData.acting_as.code
              : regData.acting_as
            : null,
      };

      const res = await apiService(API_ROUTES.UPDATE_OFFICER, "PUT", {
        body: JSON.stringify(updatedOfficerData),
        replace: { id: initialOfficerData.id },
        schema: z.object({
          id: z.string(),
        }),
      });

      if (res.status) {
        setRegData(regData);
        localSetRegData(regData);
        setFormDetails(regData);
        setIsEditing(false);
        setIsSaveDisabled(true);
        if (formResetRef.current) {
          formResetRef.current(regData);
        }
        setSuccessMessage("Personal Information has been updated.");
      }
    } catch (error) {
      console.error("Failed to update data", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    type,
    regData,
    isEditing,
    localSetRegData,
    formResetRef,
    setIsEditing,
    successMessage,
    setSuccessMessage,
    isSaveDisabled,
    setIsSaveDisabled,
    isLoading,
    setIsLoading,
    showDialog,
    setShowDialog,
    handleEditClick,
    handleDiscardClick,
    handleSaveClick,
    btnToggle,
    setBtnToggle,
    formDetails,
    setFormDetails,
    isAuditSidebarVisible,
    setAuditSidebarVisible,
  };
};

export default useOfficerPersonalInfo;
